import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ArrowRight,
  BarChart3,
  Beaker,
  BookOpen,
  Bot,
  Cable,
  ClipboardList,
  CreditCard,
  Gauge,
  Image,
  History as HistoryIcon,
  Home as HomeIcon,
  Image as ImageIcon,
  Inbox,
  Layers,
  Plug,
  Plus,
  Settings as SettingsIcon,
  Signal,
  Smartphone,
  Sparkles,
  Type as TypeIcon,
  Users,
  Video,
} from "lucide-react";
import { APP_PLATFORM_LABEL, cn } from "@/lib/utils";
import SidebarAccordion, {
  type SidebarAccordionSection,
  type SidebarAccordionSubItem,
} from "@/components/ui/SidebarAccordion";
import { useApps } from "@/hooks/useApps";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";
import { effectiveMode, systemModeLabel } from "@/lib/systemMode";
import type { AppPlatform } from "@/types/apps";

/**
 * Sidebar — 6 customer-facing top-level sections + [+ New] CTA.
 *
 * Source: docs/product/experience-and-ui.md § 11 Sidebar IA (PROD-FE-DIRECTION-2026-05-09).
 * Accordion behavior + Evidence category + always-visible quick-create
 * per § 11a / § 11b / § 11c.
 *
 * Six top-level sections per § 11b: Dashboard · App · Experiments ·
 * Evidence · Creatives · Settings. Team space is NOT a Sidebar section —
 * removed from the pinned utility rail 2026-08-18 (ENG-SWARM-ASSETS-IA-
 * POLISH item 7, supersedes 038ec7c95 "keep team space in sidebar utility
 * rail"). It stays reachable at /team via a compact arrow-terminated
 * button inside the Active-app footer block (below) and an entry in the
 * avatar dropdown (components/auth/UserMenu.tsx, between Settings and Sign
 * out) — no rail row, no rail highlight. "App" is
 * the per-app config
 * shortcut (§ 11c — Store connection · Data integrations · AI settings · Usage,
 * routing to the canonical /app pages; "App basics" → Settings →
 * App Information and "Optional product context" both removed from the App
 * group per PRODUCT-IA-RESTRUCTURE-2026-06-04 Sub-9. AI Rules + Learning Mode
 * merged into AI settings + Usage per ENG-APP-CONFIG-IA-OVERHAUL Lane 2).
 *
 * "Workflows" is NO LONGER a sidebar section (ENG-EXPERIMENT-TYPE-TAXONOMY-
 * REWORK Track C, PO 2026-07-02): a workflow is created as the Workflow
 * experiment TYPE in the New-experiment wizard (§ 5c Step 1), running/past
 * schedule rows fold into Experiments Active/History, and the pattern library
 * stays a read-only reference at `/programs/templates` linked from the wizard.
 * The `/programs/*` routes stay registered as deep links (App.tsx). Signals
 * stays an Evidence sub-route at `/evidence/signals`; the legacy `/signals`
 * URL 301-redirects.
 *
 * The Experiments accordion's first sub-item is the `+ New experiment`
 * quick-create (§ 11a); the standalone top-of-rail "+ New" button is retired
 * (Sub-9). The `n`/`w` keyboard accelerators were REMOVED 2026-06-26 (PO
 * direction) — a global single-key keydown hijacked keystrokes typed into the
 * live-connect <canvas> sign-in and blocked store connection (§ 11a); the
 * + New sub-item is the only new-object entry point.
 *
 * Honesty bound (ENG-FE-REMEDIATION-B B4): every § 11 sub-item resolves to a
 * real page — Settings → App Information (/settings/app-information) and
 * Evidence Signal Sources (/evidence/signal-sources) all render live data or
 * an honest empty state. No nav link points at a non-existent route.
 *
 * **Label convention.** Per glossary.md § Dashboard L154 + experience-and-ui.md § 11b
 * L1800, the `/` route is canonically labeled "Dashboard" in user-facing
 * copy — every customer-visible nav label, screen header, and onboarding
 * cross-link reads "Dashboard." The URL convention "Home" remains internal
 * (file names, route variables, `HomeIcon` aliases stay). Labels render
 * from the i18n catalog under `layout.sidebar.*` (ENG-MULTILOCALE-FIRST-CLASS
 * Track I2/R5 — verify_sidebar_accordion_runtime pins the en.json values);
 * sub-item casing is sentence case per the R5 normalization.
 */

interface NavSection {
  /** Stable identity for React list keys — labels are locale-varying. */
  id: string;
  section: SidebarAccordionSection;
  subItems?: SidebarAccordionSubItem[];
  isActive: (pathname: string) => boolean;
}

// The "App" section promotes per-app config pages out of Settings into a
// 1-click destination per § 11b/§ 11c: Store connection · Data integrations ·
// AI settings · Usage. The canonical routes live under /app/* (the App group
// is their navigation; rendered full-width with no Settings sub-sidebar). The
// matching /settings/* slugs stay registered as deep-link aliases
// (APP_SUBPATH_ALIASES) per § 11b — both are excluded from the Settings
// active-state so a shared route highlights under "App", not in two places at
// once. AI Rules → AI settings and Learning Mode → merged into AI settings,
// plus the new Usage page, per ENG-APP-CONFIG-IA-OVERHAUL Lane 2. Keep
// APP_SUBPATHS in sync with the App group's sub-item hrefs below.
const APP_SUBPATHS = [
  "/app/store-connections",
  "/app/integrations",
  "/app/ai-settings",
  "/app/usage",
];

// Backward-compatible /settings/* deep-link aliases for the four App config
// pages. Settings.tsx renders these identically to the /app/* canonical
// routes; they highlight the App group (never Settings) when deep-linked.
const APP_SUBPATH_ALIASES = [
  "/settings/store-connections",
  "/settings/integrations",
  "/settings/ai-rules",
  "/settings/ai-behavior",
  "/settings/ai-settings",
  "/settings/usage",
  // Retired standalone slugs — redirect into AI settings / Usage at the top of
  // Settings(), but stay excluded from the Settings highlight during redirect.
  "/settings/confidence-and-speed",
  "/settings/ai-models",
];

const GROWTH_PIPELINE_ROUTE = "/evidence/hypotheses";

const NAV_SECTION_ORDER: Record<string, number> = {
  dashboard: 0,
  experiments: 1,
  creatives: 2,
  evidence: 3,
  app: 4,
  settings: 5,
};

function buildNavSections(
  t: TFunction,
  appPlatform: AppPlatform | null,
): NavSection[] {
  const sections: NavSection[] = [
    {
      id: "dashboard",
      section: {
        label: t("layout.sidebar.sections.dashboard"),
        icon: HomeIcon,
        href: "/",
      },
      isActive: (pathname) => pathname === "/",
    },
    {
      id: "app",
      section: {
        label: t("layout.sidebar.sections.app"),
        icon: Smartphone,
        // Category, not a route (like Learnings). Click → first sub-route.
        defaultRoute: "/app/store-connections",
        alwaysOpen: true,
        disableParentState: true,
      },
      subItems: [
        {
          label: t("layout.sidebar.items.storeConnection"),
          href: "/app/store-connections",
          icon: Plug,
        },
        {
          label: t("layout.sidebar.items.dataIntegrations"),
          href: "/app/integrations",
          icon: Cable,
        },
        {
          label: t("layout.sidebar.items.aiSettings"),
          href: "/app/ai-settings",
          icon: Bot,
        },
        {
          label: t("layout.sidebar.items.usage"),
          href: "/app/usage",
          icon: Gauge,
        },
      ],
      isActive: (pathname) =>
        APP_SUBPATHS.includes(pathname) ||
        APP_SUBPATH_ALIASES.includes(pathname),
    },
    {
      id: "experiments",
      section: {
        label: t("layout.sidebar.sections.experiments"),
        icon: Beaker,
        // The existing hypothesis-pool route is the Growth pipeline category.
        // Keep the /experiments URL registered for deep links; only the
        // sidebar's landing category moves.
        defaultRoute: GROWTH_PIPELINE_ROUTE,
      },
      subItems: [
        // Quick-create as the FIRST sub-item per § 11a (mirrors `+ New workflow`
        // under Workflows; the standalone top-of-rail "+ New" button is retired).
        {
          label: t("layout.sidebar.items.newExperiment"),
          href: "/experiments/new",
          icon: Plus,
        },
        {
          label: t("layout.sidebar.items.growthPipeline"),
          href: GROWTH_PIPELINE_ROUTE,
          icon: ClipboardList,
        },
        // Decision queue (§ 5e) folds into the Active tab — no sub-item (PO
        // chat 2026-08-01 "rewire page with 'pending decisions' on
        // experiments into 'active'"; supersedes PO doc 2026-07-29 item 1's
        // standalone restore). /experiments/decisions redirects to Active.
        // Pending (§ 5g) stays retired: `pending_review` winners are Active
        // rows — sorted first and chipped "Pending review" — and the
        // AI-visual approval queue is retired outright (item 2: approval
        // happens at creation or via autopilot).
        {
          label: t("layout.sidebar.items.active"),
          href: "/experiments/active",
          icon: Sparkles,
        },
        {
          label: t("layout.sidebar.items.history"),
          href: "/experiments/history",
          icon: HistoryIcon,
        },
      ],
      isActive: (pathname) =>
        pathname.startsWith("/experiments") ||
        pathname === GROWTH_PIPELINE_ROUTE,
    },
    {
      id: "evidence",
      section: {
        label: t("layout.sidebar.sections.evidence"),
        icon: BookOpen,
        // Evidence is a sidebar CATEGORY, not a route per § 11c. Click
        // navigates to the first sub-route (Signals). Active when ANY
        // /evidence/* sub-route matches. Renamed from "Learnings"
        // (ENG-EVIDENCE-CATEGORY-RENAME); the legacy /signals + /learnings/*
        // paths 301-redirect to /evidence/* in App.tsx.
        defaultRoute: "/evidence/reports",
      },
      subItems: [
        {
          label: t("layout.sidebar.items.reports"),
          href: "/evidence/reports",
          icon: BarChart3,
        },
        {
          label: t("layout.sidebar.items.signals"),
          href: "/evidence/signals",
          icon: Signal,
        },
        // "Import" renamed to Signal sources per § 6c / Sub-6 — the manual
        // signal-input surface. The legacy /learnings/import + /learnings/
        // signal-sources paths 301-redirect to this /evidence/* canonical.
        {
          label: t("layout.sidebar.items.signalSources"),
          href: "/evidence/signal-sources",
          icon: Inbox,
        },
        // Reports (§ 6f) — the aggregate-report roof (operator direction
        // chat 2026-07-26: "implement new tab Analytics -> Reports where we
        // wire deleted Targeting pages / Keyword metric. Implement also
        // Content metrics, Channel metrics" + "and locales reports").
        //
        // Targeting pages (§ 6d) and Keyword metrics (§ 6e) stopped being
        // sidebar sub-items under ASOLOOP-UX doc item 8 ("targeting pages
        // and keyword metrics shouldn't be separate pages"; "all of them
        // are just signals") — that still holds: neither is a page or a
        // nav entry. They are TABS of this one Reports surface, joining
        // the three net-new rollups (Content / Channel / Locales), and
        // their old /evidence/* routes redirect into the matching tab so
        // deep links + the authored-CPP/CSL approve+publish queue never
        // hard-404.
      ],
      isActive: (pathname) =>
        pathname.startsWith("/evidence") && pathname !== GROWTH_PIPELINE_ROUTE,
    },
    {
      id: "creatives",
      section: {
        label: t("layout.sidebar.sections.creatives"),
        icon: Image,
        href: "/creatives",
        defaultRoute: "/creatives/screenshots",
      },
      // Track F3 platform gate, extended to the sidebar (PO 2026-08-13):
      // CPPs are an Apple surface, CSLs a Google-Play surface — the
      // wrong-platform sub-item MUST NOT appear, matching the Creatives.tsx
      // sub-tab filter. Until the platform resolves (no app selected),
      // neither platform-specific sub-item shows.
      subItems: [
        {
          label: t("layout.sidebar.items.screenshots"),
          href: "/creatives/screenshots",
          icon: ImageIcon,
        },
        {
          label: t("layout.sidebar.items.videos"),
          href: "/creatives/videos",
          icon: Video,
        },
        {
          label: t("layout.sidebar.items.icons"),
          href: "/creatives/icons",
          icon: Sparkles,
        },
        {
          label: t("layout.sidebar.items.copy"),
          href: "/creatives/copy",
          icon: TypeIcon,
        },
        ...(appPlatform === "ios"
          ? [
              {
                label: t("layout.sidebar.items.customProductPages"),
                href: "/creatives/cpp",
                icon: Layers,
              },
            ]
          : []),
        ...(appPlatform === "android"
          ? [
              {
                label: t("layout.sidebar.items.customStoreListings"),
                href: "/creatives/csl",
                icon: Layers,
              },
            ]
          : []),
        {
          label: t("layout.sidebar.items.contentRules"),
          href: "/creatives/content-rules",
          icon: ClipboardList,
        },
      ],
      isActive: (pathname) => pathname.startsWith("/creatives"),
    },
    {
      id: "settings",
      section: {
        label: t("layout.sidebar.sections.settings"),
        icon: SettingsIcon,
        href: "/settings",
        defaultRoute: "/settings/account",
        compact: true,
      },
      // Exclude the App-promoted /settings deep-link aliases so they highlight
      // under "App", not in both sections.
      isActive: (pathname) =>
        pathname.startsWith("/settings") &&
        !APP_SUBPATH_ALIASES.includes(pathname),
    },
  ];

  return sections.sort(
    (left, right) =>
      (NAV_SECTION_ORDER[left.id] ?? Number.MAX_SAFE_INTEGER) -
      (NAV_SECTION_ORDER[right.id] ?? Number.MAX_SAFE_INTEGER),
  );
}

export default function Sidebar() {
  const { t } = useTranslation();
  const { workspaceId, userId, selectedAppId } = useSelectedAppContext();
  const { data: appsData } = useApps(workspaceId, userId);
  const selectedApp =
    appsData?.apps?.find((a) => a.app_id === selectedAppId) ?? null;
  const navSections = useMemo(
    () => buildNavSections(t, selectedApp?.platform ?? null),
    [t, selectedApp?.platform],
  );
  const primaryNavSections = navSections.filter((nav) => nav.id !== "settings");
  // Keep Settings and Subscription in their own bottom rail, immediately
  // above the active-app surface, so the utility links stay visible even
  // when the primary navigation has no spare vertical space. Team space
  // is NOT part of this rail (removed 2026-08-18, ENG-SWARM-ASSETS-IA-
  // POLISH item 7) — see the Team space button in the Active-app footer
  // block below instead.
  const utilityNavSections: NavSection[] = [
    ...navSections.filter((nav) => nav.id === "settings"),
    {
      id: "subscription",
      section: {
        label: t("layout.sidebar.items.subscription"),
        icon: CreditCard,
        href: "/settings/billing",
        compact: true,
      },
      isActive: (pathname) => pathname === "/settings/billing",
    },
  ];
  // Resolve the operator-visible lifecycle mode off the SAME canonical
  // fields the Header badge uses (`system_mode` + `operator_mode_override`,
  // carried on the app-list summary) so the footer can never disagree with
  // the badge. See lib/systemMode.ts.
  const footerMode = selectedApp
    ? systemModeLabel[
        effectiveMode(
          selectedApp.system_mode,
          selectedApp.operator_mode_override,
        )
      ]
    : null;

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-gray-200 bg-white pt-14",
      )}
    >
      <nav className="sidebar-scrollbar-hidden sidebar-scroll-cue min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {primaryNavSections.map((nav) => (
          <SidebarAccordion
            key={nav.id}
            section={nav.section}
            subItems={nav.subItems}
            isActive={nav.isActive}
          />
        ))}
      </nav>
      <div className="shrink-0">
        <nav
          aria-label={t("layout.sidebar.items.utilityNavAriaLabel")}
          className="space-y-0.5 px-3 pb-2"
        >
          {utilityNavSections.map((nav) => (
            <SidebarAccordion
              key={nav.id}
              section={nav.section}
              subItems={nav.subItems}
              isActive={nav.isActive}
            />
          ))}
        </nav>
        {selectedApp && (
          <div className="border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
            <div className="font-medium uppercase tracking-wide text-muted">
              {t("layout.sidebar.footer.activeApp")}
            </div>
            {/* Operator-facing brand label — the canonical app identity every
              other surface renders (Header app-switcher, Apps Tree, Login
              Modal); never display_name, which can equal the numeric store_id
              until a baseline is scraped, so the footer would show a raw store
              id while the switcher above it shows the brand name for the same
              app (ENG-APP-CONFIG-IA-OVERHAUL Track A; PO 2026-06-16). */}
            <div className="mt-1">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-gray-900">
                  {selectedApp.brand_label}
                </div>
                <div className="mt-0.5">
                  {APP_PLATFORM_LABEL[selectedApp.platform]}
                  {footerMode !== null && (
                    <>
                      {" · "}
                      {t("layout.sidebar.footer.modeChip", {
                        mode: footerMode,
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Team space — relocated out of the rail into this footer
              block 2026-08-18 (ENG-SWARM-ASSETS-IA-POLISH item 7,
              supersedes 038ec7c95 "keep team space in sidebar utility
              rail"). Compact, quiet, arrow-terminated affordance mirroring
              an "All apps →" style footer link; highlighted when /team is
              the active route. */}
            <NavLink
              to="/team"
              className={({ isActive: navActive }) =>
                cn(
                  "mt-2 flex w-full items-center justify-between rounded-md px-2 py-1.5 font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                  navActive
                    ? "bg-primary-50 text-primary-text"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                )
              }
            >
              <span className="flex items-center gap-2">
                <Users size={14} className="shrink-0" aria-hidden="true" />
                {t("layout.sidebar.footer.teamSpace")}
              </span>
              <ArrowRight size={14} className="shrink-0" aria-hidden="true" />
            </NavLink>
          </div>
        )}
      </div>
    </aside>
  );
}
